import { DEFAULT_CHAT_PANEL_WIDTH } from "@/components/CaseChat/constants";
import { useCallback, useEffect, useState } from "react";

const CHAT_PANEL_WIDTH_CSS_VAR = "--chat-panel-width";

export const useCaseChatPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [width, setWidth] = useState(DEFAULT_CHAT_PANEL_WIDTH);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  // Forces a reset even when activeConversationId is already null (e.g. a second "New chat").
  const [newChatToken, setNewChatToken] = useState(0);

  // Expose the panel width to CSS while open (so sibling panels can shift over),
  // and clear it whenever the panel closes or unmounts.
  useEffect(() => {
    const { style } = document.documentElement;
    if (!isOpen) {
      style.removeProperty(CHAT_PANEL_WIDTH_CSS_VAR);
      return undefined;
    }
    style.setProperty(CHAT_PANEL_WIDTH_CSS_VAR, `${width}px`);
    return () => {
      style.removeProperty(CHAT_PANEL_WIDTH_CSS_VAR);
    };
  }, [isOpen, width]);

  const openChatPanel = useCallback(() => setIsOpen(true), []);
  const closeChatPanel = useCallback(() => setIsOpen(false), []);

  /** Opens the panel pinned to a specific past conversation (picked from the Chats tab). */
  const openConversation = useCallback((conversationId: string) => {
    setActiveConversationId(conversationId);
    setIsOpen(true);
  }, []);

  const setChatPanelWidth = useCallback((nextWidth: number) => setWidth(nextWidth), []);

  /** Clears the active conversation; does not itself open or close the panel. */
  const startNewChat = useCallback(() => {
    setActiveConversationId(null);
    setNewChatToken((token) => token + 1);
  }, []);

  return {
    chatPanel: { isOpen, width, activeConversationId, newChatToken },
    openChatPanel,
    openConversation,
    closeChatPanel,
    setChatPanelWidth,
    startNewChat,
  };
};
